import { useLayoutEffect, useRef, useState } from 'react'

import { FvLinkButton } from '@fv/client-components'
import { fvDateUtc } from '@fv/dates'

import { DividedContent } from '@/components/DividedContent'
import { MouseWheelAnimation } from '@/components/MouseWheelAnimation'
import { routes } from '@/routes'

import type { Audit } from './types'

type AuditCardHeaderProps = { data: Audit }

// Using zero-width space (&#8203;) to fix bug with double-click select
// https://github.com/freightview/freightview/issues/20350#issuecomment-1031586337
const AuditCardHeader = ({ data }: AuditCardHeaderProps) => {
  const {
    loadId,
    pickupDate,
    quote,
    refNums,
    trackingNumber,
    workflow,
    deliveryDate,
    acceptedDate,
  } = data

  const formattedDate = fvDateUtc(pickupDate).format('ddd MMM D')
  const formattedDeliveryDate = fvDateUtc(deliveryDate).format('ddd MMM D')
  const dividedContentRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)

  useLayoutEffect(() => {
    const el = dividedContentRef.current
    if (!el) return
    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth)
    }
    checkOverflow()
    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [])

  return (
    <div className="flex items-center">
      <DividedContent
        ref={dividedContentRef}
        className="flex-1 whitespace-nowrap min--0 overflow-auto pb-4"
      >
        <FvLinkButton
          theme="underlined"
          className="text-xl"
          rel="noreferrer"
          target="_blank"
          to={routes.details(loadId)}
        >
          {composeLinkText(data)}
        </FvLinkButton>

        {workflow !== 'parcel' && trackingNumber && (
          <span>&#8203;PRO# {trackingNumber}</span>
        )}

        {refNums.map(n => (
          <span key={n.value}>&#8203;Ref# {n.value}</span>
        ))}

        <span>&#8203;{quote.providerName ?? quote.assetCarrierName}</span>
        <span>&#8203;Pickup: {formattedDate}</span>
        {deliveryDate && <span>&#8203;Delivery: {formattedDeliveryDate}</span>}
        {acceptedDate && (
          <span>
            &#8203;Accepted: {fvDateUtc(acceptedDate).format('ddd MMM D')}
          </span>
        )}
      </DividedContent>

      {hasOverflow && <MouseWheelAnimation className="!relative ml-4" />}
    </div>
  )
}

const composeLinkText = (audit: Audit) => {
  const { bolNum, trackingNumber, workflow } = audit

  if (workflow === 'parcel' && trackingNumber) {
    return `Tracking# ${trackingNumber}`
  }

  return `BOL# ${bolNum || 'N/A'}`
}

export default AuditCardHeader
